/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import {
  ALL_FILTER_VALUE,
  PERQ_CHECK_IN_OPTIONS,
  PERQ_DISCOUNT_CLASSES,
  PERQ_ORDER_TYPES,
  PERQ_REPORT_DATE_FILTERS,
  defaultPerqFilters,
  sanitizeNameList,
  sanitizeStringList,
} from "../../../utils/perqReportDrilldown";
import {
  fromSavedDate,
  savedOptionId,
  savedOptionList,
  toSavedDate,
  toSavedOption,
  toSavedOptions,
} from "../../../components/saved_queries/savedFilterHelpers";

const CUSTOM_DATE = "Custom Date";
const noLabel = () => null;

const selectedId = (value) => (value === ALL_FILTER_VALUE || value === null || value === undefined ? null : value);

/**
 * Perq Report filters <-> saved query JSON.
 * lookups: { restaurantGroupName, subGroupName, restaurantName }, each id => label.
 */
export const perqSavedQueryAdapter = {
  version: 1,
  defaults: defaultPerqFilters,

  serialize: (filters, lookups = {}) => {
    const isCustom = filters.dateFilter === CUSTOM_DATE;
    const groupId = selectedId(filters.restaurant_group_id);
    const subGroupId = selectedId(filters.restaurant_sub_group_id);
    const restaurantIds = (filters.restaurant_ids || []).filter((id) => id !== ALL_FILTER_VALUE);

    return {
      dateFilter: filters.dateFilter,
      customDateFrom: isCustom ? toSavedDate(filters.customDateFrom) : null,
      customDateTo: isCustom ? toSavedDate(filters.customDateTo) : null,
      restaurant_group: groupId === null ? null : toSavedOption(groupId, lookups.restaurantGroupName?.(groupId)),
      restaurant_sub_group:
        subGroupId === null ? null : toSavedOption(subGroupId, lookups.subGroupName?.(subGroupId)),
      restaurants: toSavedOptions(restaurantIds, lookups.restaurantName || noLabel),
      discounts: sanitizeStringList(filters.discounts, PERQ_DISCOUNT_CLASSES),
      discount_names: sanitizeNameList(filters.discount_names).map((name) => toSavedOption(name, name)),
      check_in: filters.check_in,
      order_type: filters.order_type,
      // Trimmed like the search the report runs, so a trailing space doesn't count as unapplied.
      search: (filters.search || "").trim(),
    };
  },

  deserialize: (saved) => {
    const source = saved && typeof saved === "object" ? saved : {};
    const defaults = defaultPerqFilters();

    const dateFilter = PERQ_REPORT_DATE_FILTERS.includes(source.dateFilter) ? source.dateFilter : defaults.dateFilter;
    const isCustom = dateFilter === CUSTOM_DATE;
    const group = savedOptionId(source.restaurant_group) === null ? null : source.restaurant_group;
    // A sub-group only exists inside its group.
    const subGroup = group && savedOptionId(source.restaurant_sub_group) !== null ? source.restaurant_sub_group : null;
    const restaurants = savedOptionList(source.restaurants)
      .filter((option) => Number.isInteger(Number(option.id)))
      .map((option) => ({ id: Number(option.id), label: option.label }));
    const discountNames = sanitizeNameList(savedOptionList(source.discount_names).map((option) => option.id));

    return {
      filters: {
        dateFilter,
        customDateFrom: isCustom ? fromSavedDate(source.customDateFrom) : defaults.customDateFrom,
        customDateTo: isCustom ? fromSavedDate(source.customDateTo) : defaults.customDateTo,
        restaurant_group_id: group ? group.id : ALL_FILTER_VALUE,
        restaurant_sub_group_id: subGroup ? subGroup.id : ALL_FILTER_VALUE,
        restaurant_ids: restaurants.length ? restaurants.map((option) => option.id) : [ALL_FILTER_VALUE],
        discounts: sanitizeStringList(source.discounts, PERQ_DISCOUNT_CLASSES),
        discount_names: discountNames,
        check_in: PERQ_CHECK_IN_OPTIONS.includes(source.check_in) ? source.check_in : ALL_FILTER_VALUE,
        order_type: PERQ_ORDER_TYPES.includes(source.order_type) ? source.order_type : defaults.order_type,
        search: typeof source.search === "string" ? source.search : "",
      },
      labelOptions: { restaurant_group: group, restaurant_sub_group: subGroup, restaurants },
    };
  },
};
